import React from "react";

import { useUploadMultipleImage } from "@hooks";
import { DocumentData, WithFieldValue } from "firebase/firestore";
import { Alert, ButtonProps, Text } from "@chakra-ui/react";
import { useAuth, useCustomToast } from "@hooks";
import { ref, getStorage, getDownloadURL } from "firebase/storage";
import { ButtonModal, JsonForm, JsonFormControl } from "@components";

type AddMultipleImageModalProps = ButtonProps & {
  itemId: string;
};

const AddMultipleImageFormFields: JsonFormControl[] = [
  {
    label: "CV Image(s)",
    inputType: "multipleimage",
    fieldName: "images",
    defaultValue: undefined,
    config: {
      required: "Image is required",
    },
  },
];

export function AddMultipleImageModal({
  itemId,
  ...delegated
}: AddMultipleImageModalProps) {
  const authData = useAuth();

  const [error] = React.useState("");

  // Beginning of comment for tag.
  // This allows us to search for this text to avoid writing duplicate tags.
  const commentIV = "Import IV curve from CERN DB";

  const storage = getStorage();
  const upload = useUploadMultipleImage();

  const imageAddedToast = useCustomToast(() => (
    <Alert status="success">Images Added!</Alert>
  ));

  const doSubmit = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      for (const image of data.images) {
        const imageId = image.name.split(".png")[0];
        // This isn't very elegant, but it checks if the image already exists.
        // If it doesn't, an error is thrown, and the image is added.
        try {
          await getDownloadURL(ref(storage, "images/" + imageId + ".png"));
        } catch (err) {
          await upload(image, {
            title: imageId,
            comments: commentIV,
            module: imageId,
            userId: authData.user?.firebaseUser?.uid || "",
            userName:
              authData.user?.firstName && authData.user?.lastName
                ? `${authData.user.firstName} ${authData.user.lastName}`
                : "",
          });
        }
        imageAddedToast();
      }
    },
    [itemId],
  );

  return (
    <ButtonModal label="Add Images" title="Add Images" size="sm" {...delegated}>
      <JsonForm
        fields={AddMultipleImageFormFields}
        onSubmit={doSubmit}
        clearAfterSubmit
      />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
